import { Box } from '@mui/material';
import { JSX, useState } from 'react';

import { Alert } from 'Components/Alert/Alert';
import { Button } from 'Components/Buttons/Buttons';
import { FormFieldText } from 'Components/FormField/FormFieldText/FormFieldText';
import { FormFieldTextArea } from 'Components/FormField/FormFieldTextArea/FormFieldTextArea';
import { Modal } from 'Components/Modal/Modal';
import { ICON_CLOSE } from 'Config/Icons';
import { DueDiligenceControlGroupConfiguration } from 'Models/DueDiligence';

import styles from './Modal.module.css';

export interface ControlGroupModalProps {
    existingNames: string[];
    hideModal: () => void;
    onSave: (group: DueDiligenceControlGroupConfiguration) => void;

    /** If provided, the modal is in edit mode with this group pre-populated. */
    group?: DueDiligenceControlGroupConfiguration;
}

/**
 * Modal for adding or editing a control group.
 * In add mode: Creates a new control group with empty controls.
 * In edit mode: Updates the control group's name and description.
 */
export const ControlGroupModal = (props: ControlGroupModalProps): JSX.Element => {
    const { existingNames, group, hideModal, onSave } = props;
    const isEditMode = !!group;

    const [name, setName] = useState(group?.name ?? '');
    const [description, setDescription] = useState(group?.description ?? '');
    const [errorMessage, setErrorMessage] = useState<string>();

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setName(event.target.value);
    };

    const handleDescriptionChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
        setDescription(event.target.value);
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
        event.preventDefault();
        setErrorMessage(undefined);

        const trimmedName = name.trim();
        const trimmedDescription = description.trim();

        if (trimmedName.length === 0) {
            setErrorMessage('Name is required.');
            return;
        }

        if (existingNames.includes(trimmedName)) {
            setErrorMessage('A control group with this name already exists in this framework.');
            return;
        }

        if (trimmedDescription.length === 0) {
            setErrorMessage('Description is required.');
            return;
        }

        const updatedGroup: DueDiligenceControlGroupConfiguration = {
            controls: group?.controls ?? [],
            description: trimmedDescription,
            name: trimmedName,
        };

        onSave(updatedGroup);
    };

    return (
        <Modal open header={isEditMode ? 'Edit Control Group' : 'Add Control Group'}>
            {errorMessage && <Alert type="error">{errorMessage}</Alert>}
            <Box component="form" noValidate onSubmit={handleSubmit}>
                <div className={styles.formField}>
                    <FormFieldText formFieldId="name" formFieldLabel="Name" value={name} handleChange={handleNameChange} required />
                </div>

                <div className={styles.formField}>
                    <FormFieldTextArea formFieldId="description" formFieldLabel="Description" value={description} handleChange={handleDescriptionChange} required />
                </div>

                <div className={'modalFormButtonContainer'}>
                    <Button variant="secondary" onClick={hideModal} fontAwesomeImage={ICON_CLOSE}>
                        Cancel
                    </Button>
                    <Button variant="submit">Save</Button>
                </div>
            </Box>
        </Modal>
    );
};
